import React from 'react'
import { connect } from 'react-redux'
import { getSelectedStore } from 'x/redux/selectors'
import dayjs from 'dayjs'

import { ScrollView, TouchableOpacity } from 'react-native'

import * as util from 'x/utils/util'
import { DEFAULT_QTY_ADJUSTMENT_HISTORY_FILTER, mergeWithDefaultFilter } from 'x/utils/inventoryFilterUtils'

import VStack from 'xui/components/VStack'
import HStack from 'xui/components/HStack'
import XText from 'xui/components/XText'
import XContainer from 'xui/components/XContainer'
import XCustomHeader from 'xui/components/XCustomHeader'
import XInput from 'xui/components/XInput'
import XButton from 'xui/components/XButton'
import XIcon from 'xui/components/XIcon'
import XCard from 'xui/components/XCard'
import XDateRangeSelectorButton from 'xui/components/XDateRangeSelectorButton'
import Segment from 'xui/components/Segment'

import {
  IQtyAdjustmentHistoryListFilterViewProps,
  IQtyAdjustmentHistoryListFilterViewState,
  IQtyAdjustmentMode,
  IQtyAdjustmentHistoryFilterState,
  IXDateRangeSelectorChangeParams,
} from 'x/types'

const MODE_OPTIONS: { key: IQtyAdjustmentMode; label: string; colorScheme: string }[] = [
  { key: 'a', label: 'เพิ่ม', colorScheme: 'success' },
  { key: 'd', label: 'ลด', colorScheme: 'danger' },
  { key: 's', label: 'ปรับ', colorScheme: 'purple' },
  { key: 'c', label: 'นับ', colorScheme: 'info' },
]

const DATE_RANGE_OPTIONS = ['ทั้งหมด', 'ระบุ']

class QtyAdjustmentHistoryListFilterView extends React.Component<
  IQtyAdjustmentHistoryListFilterViewProps,
  IQtyAdjustmentHistoryListFilterViewState
> {
  static displayName = 'QtyAdjustmentHistoryListFilterView'

  defaultFilter: IQtyAdjustmentHistoryFilterState

  constructor(props: IQtyAdjustmentHistoryListFilterViewProps) {
    super(props)

    // Get nav params
    const navDefaultFilter = util.getNavParam(props, 'defaultFilter')
    const navCurrentFilter = util.getNavParam(props, 'currentFilter')

    // Merge default filter
    this.defaultFilter = mergeWithDefaultFilter(navDefaultFilter, DEFAULT_QTY_ADJUSTMENT_HISTORY_FILTER)

    // Merge current filter with default
    const currentFilter = mergeWithDefaultFilter(navCurrentFilter, this.defaultFilter)

    this.state = {
      isInitialized: false,
      searchText: currentFilter.searchText || '',
      selectedMode: currentFilter.selectedMode || null,
      isDateRangeEnabled: currentFilter.isDateRangeEnabled || false,
      dateRangeFrom: currentFilter.dateRangeFrom,
      dateRangeTo: currentFilter.dateRangeTo,
      selectedOptionKey: currentFilter.selectedOptionKey || 'Today',
    }
  }

  async componentDidMount() {
    await util.setStatePromise(this, { isInitialized: true })
  }

  _goBack = () => {
    util.navGoBack(this.props)
  }

  _onApplyFilter = () => {
    const { searchText, selectedMode, isDateRangeEnabled, dateRangeFrom, dateRangeTo, selectedOptionKey } = this.state
    const onApplyFilter = util.getNavParam(this.props, 'onApplyFilter')

    if (onApplyFilter) {
      // Build complete filter object
      const filter: IQtyAdjustmentHistoryFilterState = {
        searchText: searchText?.trim() || '',
        selectedMode,
        isDateRangeEnabled,
        dateRangeFrom: isDateRangeEnabled ? dateRangeFrom : undefined,
        dateRangeTo: isDateRangeEnabled ? dateRangeTo : undefined,
        selectedOptionKey: isDateRangeEnabled ? selectedOptionKey : undefined,
      }

      onApplyFilter(filter)
    }

    this._goBack()
  }

  _onResetFilter = async () => {
    // Reset to default filter
    await util.setStatePromise(this, {
      searchText: this.defaultFilter.searchText || '',
      selectedMode: this.defaultFilter.selectedMode || null,
      isDateRangeEnabled: this.defaultFilter.isDateRangeEnabled || false,
      dateRangeFrom: this.defaultFilter.dateRangeFrom,
      dateRangeTo: this.defaultFilter.dateRangeTo,
      selectedOptionKey: this.defaultFilter.selectedOptionKey || 'Today',
    })
  }

  _onSearchTextChange = (text: string) => {
    this.setState({ searchText: text })
  }

  _onSelectMode = (mode: IQtyAdjustmentMode) => {
    const { selectedMode } = this.state
    // Toggle: if same mode is selected, deselect it
    if (selectedMode === mode) {
      this.setState({ selectedMode: null })
    } else {
      this.setState({ selectedMode: mode })
    }
  }

  _onDateRangeSegmentChange = (index: number) => {
    const isEnabled = index === 1
    this.setState({ isDateRangeEnabled: isEnabled })
  }

  _onDateRangeChange = (params: IXDateRangeSelectorChangeParams) => {
    const { dates, optionKey } = params
    this.setState({
      dateRangeFrom: dates?.begin ? dates.begin.format('YYYY-MM-DDTHH:mm:ssZ') : undefined,
      dateRangeTo: dates?.end ? dates.end.format('YYYY-MM-DDTHH:mm:ssZ') : undefined,
      selectedOptionKey: optionKey,
    })
  }

  _renderCustomHeader = () => (
    <XCustomHeader title='ตัวกรอง ประวัติการปรับจำนวนสินค้า' headerLeftProps={{ backIcon: true, onPressItem: this._goBack }} />
  )

  _renderSearchTextCard = () => {
    const { searchText } = this.state

    return (
      <XCard w='full' p='3'>
        <VStack w='full' space='2'>
          <XText bold>ค้นหา</XText>
          {/* <HStack w='full' alignItems='center' bg='muted.100' borderRadius='lg' px='2'> */}
          <XInput
            flex={1}
            placeholder='ค้นหาด้วย ชื่อสินค้า, รหัสอ้างอิง, หมายเหตุ...'
            value={searchText}
            onChangeText={this._onSearchTextChange}
            InputLeftElement={
              <HStack w='34px' alignItems='center' justifyContent='center'>
                <XIcon name='search' family='FontAwesome' size='sm' color='muted.400' />
              </HStack>
            }
            InputRightElement={
              searchText.length > 0 ? (
                <TouchableOpacity onPress={() => this.setState({ searchText: '' })}>
                  <XIcon name='close-circle' family='Ionicons' size='sm' color='muted.400' />
                </TouchableOpacity>
              ) : null
            }
          />
          {/* </HStack> */}
        </VStack>
      </XCard>
    )
  }

  _renderModeFilterCard = () => {
    const { selectedMode } = this.state

    return (
      <XCard w='full' p='3'>
        <VStack w='full' space='2'>
          <XText bold>ประเภทการปรับ</XText>
          <HStack w='full' flexWrap='wrap' space='2'>
            {MODE_OPTIONS.map((opt) => {
              const isSelected = selectedMode === opt.key
              return (
                <TouchableOpacity key={opt.key} onPress={() => this._onSelectMode(opt.key)}>
                  <HStack
                    px='3'
                    py='2'
                    mb='2'
                    borderRadius='lg'
                    borderWidth='2'
                    borderColor={isSelected ? `${opt.colorScheme}.500` : 'muted.300'}
                    bg={isSelected ? `${opt.colorScheme}.500` : 'white'}
                    alignItems='center'
                    space='1'
                  >
                    {isSelected && <XIcon name='check' family='FontAwesome' size='xs' color='white' />}
                    <XText color={isSelected ? 'white' : 'muted.600'} bold={isSelected}>
                      {opt.label}
                    </XText>
                  </HStack>
                </TouchableOpacity>
              )
            })}
          </HStack>
        </VStack>
      </XCard>
    )
  }

  _renderDateRangeCard = () => {
    const { isDateRangeEnabled, dateRangeFrom, dateRangeTo, selectedOptionKey } = this.state
    const selectedIndex = isDateRangeEnabled ? 1 : 0

    const selectedDates =
      dateRangeFrom && dateRangeTo
        ? {
            begin: dayjs(dateRangeFrom),
            end: dayjs(dateRangeTo),
          }
        : util.getDateRangeFromOption(selectedOptionKey || 'Today')

    return (
      <XCard w='full' p='3'>
        <VStack w='full' space='2'>
          <XText bold>ช่วงเวลาดำเนินการ</XText>
          <Segment options={DATE_RANGE_OPTIONS} selectedIndex={selectedIndex} onSegmentChange={this._onDateRangeSegmentChange} />
          {isDateRangeEnabled && (
            <XDateRangeSelectorButton
              selectedDates={selectedDates}
              selectedOptionKey={selectedOptionKey || 'Today'}
              onChange={this._onDateRangeChange}
              showTime
            />
          )}
        </VStack>
      </XCard>
    )
  }

  _renderResetButton = () => (
    <TouchableOpacity onPress={this._onResetFilter}>
      <HStack w='full' justifyContent='center' py='2'>
        <XText variant='inactive' textDecorationLine='underline'>
          รีเซ็ตตัวกรอง
        </XText>
      </HStack>
    </TouchableOpacity>
  )

  _renderApplyButton = () => (
    <HStack w='full' p='2' bg='white' borderTopWidth='1' borderTopColor='muted.200' space='2'>
      {/* {this._renderResetButton()} */}
      <XButton h='34px' w='150px' variant='outline' onPress={this._onResetFilter} colorScheme='muted'>
        รีเซ็ตตัวกรอง
      </XButton>

      <XButton h='34px' flex={1} onPress={this._onApplyFilter} colorScheme='primary'>
        นำไปใช้
      </XButton>
    </HStack>
  )

  render() {
    const { isInitialized } = this.state

    if (!isInitialized) {
      return <XContainer>{this._renderCustomHeader()}</XContainer>
    }

    return (
      <XContainer>
        {this._renderCustomHeader()}
        <ScrollView style={{ flex: 1 }}>
          <VStack w='full' p='2' space='2'>
            {this._renderSearchTextCard()}
            {this._renderModeFilterCard()}
            {this._renderDateRangeCard()}
          </VStack>
        </ScrollView>
        {this._renderApplyButton()}
      </XContainer>
    )
  }
}

export default connect(
  (state) => ({
    selectedStore: getSelectedStore(state),
  }),
  (dispatch) => ({ dispatch })
)(QtyAdjustmentHistoryListFilterView)
